import React, { useState, useEffect } from 'react';
import { companiesApi, importApi } from '../lib/api';
import { formatDate, formatCurrency, PROCUREMENT_SECTORS } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Plus, MagnifyingGlass, Pencil, Trash, Globe, MapPin, Phone, Users, Upload, Download 
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function CompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    website: '',
    address: '',
    city: '',
    country: '',
    phone: '',
    size: '',
    annual_revenue: 0,
    sector: '',
    notes: ''
  });

  useEffect(() => {
    fetchCompanies();
  }, [search, sectorFilter]);

  const fetchCompanies = async () => {
    try {
      const response = await companiesApi.getAll({ 
        search, 
        sector: sectorFilter || undefined,
        limit: 100 
      });
      setCompanies(response.data);
    } catch (error) {
      toast.error('Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCompany) {
        await companiesApi.update(editingCompany.id, formData);
        toast.success('Company updated');
      } else {
        await companiesApi.create(formData);
        toast.success('Company created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save company');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this company?')) return;
    try {
      await companiesApi.delete(id);
      toast.success('Company deleted');
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to delete company');
    }
  };

  const openEditDialog = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      industry: company.industry || '',
      website: company.website || '',
      address: company.address || '',
      city: company.city || '',
      country: company.country || '',
      phone: company.phone || '',
      size: company.size || '',
      annual_revenue: company.annual_revenue || 0,
      sector: company.sector || '',
      notes: company.notes || ''
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCompany(null);
    setFormData({
      name: '',
      industry: '',
      website: '',
      address: '',
      city: '',
      country: '',
      phone: '',
      size: '',
      annual_revenue: 0,
      sector: '',
      notes: ''
    });
  };

  const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];

  const handleCsvImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const response = await importApi.importCompanies(formData);
      toast.success(`Imported ${response.data.imported} companies`);
      if (response.data.errors?.length > 0) {
        toast.error(`${response.data.errors.length} rows had errors`);
      }
      setIsImportOpen(false);
      setImportFile(null);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const csv = 'name,industry,website,address,city,country,phone,size,annual_revenue,sector,notes\nAcme Corp,Technology,https://acme.com,123 Main St,New York,USA,+1234567890,51-200,5000000,services,Key client\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'companies_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="companies-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Companies</h1>
          <p className="text-muted-foreground text-sm mt-1">{companies.length} companies</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setIsImportOpen(true)} 
            className="rounded-sm"
            data-testid="import-companies-btn"
          >
            <Upload size={18} className="mr-2" />
            Import CSV
          </Button>
          <Button 
            onClick={() => { resetForm(); setIsDialogOpen(true); }} 
            className="rounded-sm"
            data-testid="add-company-btn"
          >
            <Plus size={18} className="mr-2" />
            Add Company
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            placeholder="Search companies..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="search-companies"
          />
        </div>
        <Select value={sectorFilter || "all"} onValueChange={(v) => setSectorFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48" data-testid="sector-filter">
            <SelectValue placeholder="All Sectors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sectors</SelectItem>
            {PROCUREMENT_SECTORS.map((sector) => (
              <SelectItem key={sector.id} value={sector.id}>{sector.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Companies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full p-8 text-center text-muted-foreground">Loading...</div>
        ) : companies.length === 0 ? (
          <div className="col-span-full p-8 text-center text-muted-foreground">No companies found</div>
        ) : (
          companies.map((company) => (
            <div
              key={company.id}
              className="bg-card border border-border p-4 rounded-sm hover:-translate-y-[1px] transition-transform"
              data-testid={`company-card-${company.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold">{company.name}</h3>
                  {company.industry && (
                    <p className="text-sm text-muted-foreground">{company.industry}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditDialog(company)}
                    data-testid={`edit-company-${company.id}`}
                  >
                    <Pencil size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(company.id)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`delete-company-${company.id}`}
                  >
                    <Trash size={14} />
                  </Button>
                </div>
              </div>

              {company.sector && (
                <Badge variant="outline" className="mb-3 text-xs">
                  {PROCUREMENT_SECTORS.find(s => s.id === company.sector)?.label || company.sector}
                </Badge>
              )}

              <div className="space-y-2 text-sm">
                {company.website && (
                  <div className="flex items-center gap-2">
                    <Globe size={14} className="text-muted-foreground" />
                    <a href={company.website} target="_blank" rel="noopener noreferrer" className="hover:text-primary truncate">
                      {company.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
                {(company.city || company.country) && (
                  <div className="flex items-center gap-2">
                    <MapPin size={14} className="text-muted-foreground" />
                    <span>{[company.city, company.country].filter(Boolean).join(', ')}</span>
                  </div>
                )}
                {company.phone && (
                  <div className="flex items-center gap-2">
                    <Phone size={14} className="text-muted-foreground" />
                    <span>{company.phone}</span>
                  </div>
                )}
                {company.size && (
                  <div className="flex items-center gap-2">
                    <Users size={14} className="text-muted-foreground" />
                    <span>{company.size} employees</span>
                  </div>
                )}
              </div>

              {company.annual_revenue > 0 && (
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">Annual Revenue</p>
                  <p className="font-semibold">{formatCurrency(company.annual_revenue)}</p>
                </div>
              )}

              <p className="text-xs text-muted-foreground mt-3">
                Added {formatDate(company.created_at)}
              </p>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCompany ? 'Edit Company' : 'Add Company'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="company-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Industry</Label>
                <Input
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  data-testid="company-industry"
                />
              </div>
              <div className="space-y-2">
                <Label>Sector</Label>
                <Select
                  value={formData.sector || "none"}
                  onValueChange={(value) => setFormData({ ...formData, sector: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="company-sector">
                    <SelectValue placeholder="Select sector" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {PROCUREMENT_SECTORS.map((sector) => (
                      <SelectItem key={sector.id} value={sector.id}>{sector.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Website</Label>
              <Input
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://example.com"
                data-testid="company-website"
              />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                data-testid="company-address"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  data-testid="company-city"
                />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  data-testid="company-country"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="company-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Company Size</Label>
                <Select
                  value={formData.size || "unknown"}
                  onValueChange={(value) => setFormData({ ...formData, size: value === "unknown" ? "" : value })}
                >
                  <SelectTrigger data-testid="company-size">
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unknown">Unknown</SelectItem>
                    {COMPANY_SIZES.map((size) => (
                      <SelectItem key={size} value={size}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Annual Revenue</Label>
              <Input
                type="number"
                min="0"
                value={formData.annual_revenue}
                onChange={(e) => setFormData({ ...formData, annual_revenue: parseFloat(e.target.value) || 0 })}
                data-testid="company-revenue"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                data-testid="company-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="company-submit">
                {editingCompany ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Import Companies from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload a CSV file with columns: name, industry, website, address, city, country, phone, size, annual_revenue, sector, notes
            </p>
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-company-template">
              <Download size={14} className="mr-2" />
              Download Template
            </Button>
            <div className="space-y-2">
              <Label>CSV File</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                data-testid="import-company-file"
              />
            </div>
            {importFile && (
              <p className="text-sm text-muted-foreground">Selected: {importFile.name}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsImportOpen(false)}>Cancel</Button>
            <Button onClick={handleCsvImport} disabled={!importFile || importing} data-testid="import-company-submit">
              {importing ? 'Importing...' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
